@if (document$ | async; as document) {
  <ng-container *ngTemplateOutlet="documentDetails; context: { $implicit: document, loading: false }"></ng-container>
} @else {
  <ng-container *ngTemplateOutlet="documentDetails; context: { $implicit: null, loading: true }"></ng-container>
}

<ng-template #documentDetails [appTemplateContext]="documentDetailsContext" let-document let-loading="loading">
  @if (!loading) {
    <div class="details-page-header-wrapper">
      <div class="page-header">
        <div class="details-page-back-title">
          <a [routerLink]="[pageReturnURL]" class="details-page-back-btn">
            <rkt-icon name="arrow-left" class="details-page-back-btn-icon"></rkt-icon>
          </a>
          <h1 class="details-page-title">{{ document?.documentTypeDisplayName }}</h1>
          @if (isVerifiedDocument(document)) {
            <rkt-icon name="verified" class="verified-icon" title="Verified Document"></rkt-icon>
          }
        </div>
        <app-breadcrumbs [itemList]="breadcrumbList"></app-breadcrumbs>
      </div>
      <div class="page-header-controls">
        <app-document-details-header
          [isControlsDisabled]="loading || !isUnverifiedDocument(document)"
          (btnClicked)="onHeaderBtnClick($event, document)"
        ></app-document-details-header>
      </div>
    </div>
  } @else {
    <app-details-page-header-placeholder [pageReturnURL]="pageReturnURL" [withStatus]="false"></app-details-page-header-placeholder>
  }

  <div class="entity-details-container">
    <div class="details-section-title">
      <div class="title">
        <div class="icon">
          @if (loading) {
            <div class="loading-placeholder"></div>
          }
          @if (!loading && document?.documentTypeIcon) {
            <rkt-icon [name]="document.documentTypeIcon!"></rkt-icon>
          }
        </div>
        <app-text-with-placeholder [showPlaceholder]="loading">{{ document?.documentTypeDisplayName }}</app-text-with-placeholder>
      </div>

      <div class="controls">
        @if (isUnverifiedDocument(document)) {
          <button
            rktButton
            btnType="link"
            class="control-btn"
            [disabled]="!!(loading | accessControl | async)"
            (click)="onEditBtnClick(document)"
          >
            Edit
          </button>
        }
      </div>
    </div>

    <div class="details-section basic-info-section">
      <div class="id-row">
        <app-text-with-placeholder [showPlaceholder]="loading">ID: {{ document?.id || '' | rktShortId }}</app-text-with-placeholder>
      </div>

      <div class="details-row details-row-with-offset">
        <app-details-item
          [class]="pageClassPrefix + 'document-name'"
          [showPlaceholder]="loading"
          label="Document Name"
          [value]="$safeNavigationMigration(document?.name)"
        ></app-details-item>

        <app-details-item
          [class]="pageClassPrefix + 'document-number'"
          [showPlaceholder]="loading"
          label="Document number"
          [value]="$safeNavigationMigration(document?.number)"
        ></app-details-item>
      </div>

      <div class="details-row details-row-with-offset">
        <app-details-item
          [class]="pageClassPrefix + 'issuing-date'"
          [showPlaceholder]="loading"
          label="Issuing Date"
          [value]="$safeNavigationMigration(document?.issuingDate) | dateFormat: 'MM/dd/yy' : 'ISO'"
        ></app-details-item>

        <app-details-item
          [class]="pageClassPrefix + 'expiry-date'"
          [showPlaceholder]="loading"
          label="Expiry Date"
          [value]="$safeNavigationMigration(document?.expiryDate) | dateFormat: 'MM/dd/yy' : 'ISO'"
        ></app-details-item>
        @if (loading || document?.documentType === 'passport') {
          <app-details-item
            [class]="pageClassPrefix + 'issuing-country'"
            [showPlaceholder]="loading"
            label="Issuing Country"
            [noBreak]="true"
            [value]="$safeNavigationMigration(document?.issuingCountryCode)"
          ></app-details-item>
        }
      </div>

      <div class="details-row">
        @if (loading || document?.documentType !== 'passport') {
          <app-details-item
            [class]="pageClassPrefix + 'issuing-country'"
            [showPlaceholder]="loading"
            label="Issuing Country"
            [noBreak]="true"
            [value]="$safeNavigationMigration(document?.issuingCountryCode)"
          ></app-details-item>
        }

        @if (loading || document?.documentType !== 'passport') {
          <app-details-item
            [class]="pageClassPrefix + 'issuing-state'"
            [showPlaceholder]="loading"
            label="Issuing State"
            [value]="$safeNavigationMigration(document?.issuingStateCode)"
          ></app-details-item>
        }
      </div>

      <div class="details-separator"></div>

      <div class="details-row description-row">
        <app-details-item
          [class]="pageClassPrefix + 'description'"
          [showPlaceholder]="loading"
          [isValueMuted]="true"
          label="Description"
          [value]="$safeNavigationMigration(document?.description)"
        ></app-details-item>
      </div>

      @if (!loading) {
        <div class="details-separator"></div>
        <div class="details-section-title document-file-title">
          <app-text-with-placeholder [showPlaceholder]="loading">Document File</app-text-with-placeholder>
        </div>
        @if (!loading) {
          <div class="file-list">
            <div class="file-item" (click)="onFilePreviewClick(document)">
              <div class="file-name">
                <rkt-icon [name]="document.documentTypeIcon!"></rkt-icon>
                {{ document?.documentTypeDisplayName }}{{ isTwoSided(document) ? ' (Front)' : '' }}
              </div>
              <div class="file-image">
                <img
                  [src]="'./assets/images/' + $safeNavigationMigration(document?.documentTypeIcon) + '-placeholder.jpg'"
                  [alt]="$safeNavigationMigration(document?.documentTypeDisplayName)"
                />
                <div class="banner-text">{{ getDocumentScanLabel(document) }}</div>
              </div>
            </div>

            @if (isTwoSided(document)) {
              <div class="file-item" (click)="onBackFilePreviewClick(document)">
                <div class="file-name">
                  <rkt-icon [name]="document.documentTypeIcon!"></rkt-icon>
                  {{ document?.documentTypeDisplayName }} (Back)
                </div>
                <div class="file-image">
                  <img
                    [src]="'./assets/images/' + $safeNavigationMigration(document?.documentTypeIcon) + '-placeholder.jpg'"
                    [alt]="$safeNavigationMigration(document?.documentTypeDisplayName)"
                  />
                  <div class="banner-text">{{ getBackScanLabel(document) }}</div>
                </div>
              </div>
            }
          </div>
        }
      }
    </div>

    <div class="details-separator"></div>

    <div class="create-update-section">
      <div class="details-row">
        <app-details-item
          [class]="pageClassPrefix + 'created-at'"
          [showPlaceholder]="loading"
          label="Created At"
          [value]="$safeNavigationMigration(document?.createdAt) | dateFormat"
        ></app-details-item>

        <app-details-item
          [class]="pageClassPrefix + 'created-by'"
          [showPlaceholder]="loading"
          label="Created By"
          [value]="$safeNavigationMigration(document?.createdBy?.id) | teamMemberName | async"
        ></app-details-item>
      </div>

      <div class="details-row">
        <app-details-item
          [class]="pageClassPrefix + 'updated-at'"
          [showPlaceholder]="loading"
          label="Updated At"
          [value]="$safeNavigationMigration(document?.updatedAt) | dateFormat"
        ></app-details-item>

        <app-details-item
          [class]="pageClassPrefix + 'updated-by'"
          [showPlaceholder]="loading"
          label="Updated By"
          [value]="$safeNavigationMigration(document?.updatedBy?.id) | teamMemberName | async"
        ></app-details-item>
      </div>
    </div>
  </div>
</ng-template>
